import React from 'react'
import { runCommand } from './Effects'
import { getCurrentTimestamp } from './Utilities'
import { enqueueCommand, useElmishWithReinspect } from '@utilities/useElmish'

// @ts-ignore
export const Store = React.createContext()

const PROPERTY_INFO_POLL_INTERVAL = 100
const MAX_PROPERTY_INFO_AGE = 300

const initialState = {
  mode: 'Ready',
  properties: {},
  propertiesCount: 0,
  allPropertySummariesLoaded: false,
  errorMessage: null
}

function loadPropertyDetails(state: any, propertyDetails: any, timestamp: any) {
  return {
    ...state,
    properties: {
      ...state.properties,
      [propertyDetails.id]: {
        ...(state.properties[propertyDetails.id] || {}),
        ...propertyDetails,
        detailsTimestamp: timestamp
      }
    }
  }
}

function loadAllPropertySummaries(state: any, propertySummaries: any, timestamp: any) {
  let updatedProperties = { ...state.properties }
  propertySummaries.forEach((property: any) => {
    updatedProperties[property.id] = {
      ...(updatedProperties[property.id] || {}),
      ...property,
      summaryTimestamp: timestamp
    }
  })
  return {
    ...state,
    properties: updatedProperties,
    propertiesCount: propertySummaries.length,
    allPropertySummariesLoaded: true
  }
}

function reducer(state: any, action: any) {
  switch (action.type) {
  case 'REQUIRE_PROPERTY_DETAILS':
    if (state.mode !== 'Ready') {
      return state
    }
    if (Object.prototype.hasOwnProperty.call(state.properties, action.propertyId)) {
      if (state.properties[action.propertyId].detailsTimestamp) {
        return state
      }
    }
    return {
      ...enqueueCommand(state, {
        type: 'CMD_FETCH_PROPERTY_DETAILS',
        propertyId: action.propertyId
      }),
      mode: 'Loading'
    }

  case 'REQUIRE_ALL_PROPERTY_SUMMARIES':
    if (state.mode !== 'Ready' || state.allPropertySummariesLoaded) {
      return state
    }
    return {
      ...enqueueCommand(state, {
        type: 'CMD_FETCH_ALL_PROPERTY_SUMMARIES'
      }),
      mode: 'Loading'
    }

  case 'LOAD_PROPERTY_DETAILS':
    if (state.mode !== 'Loading') {
      return state
    }
    return loadPropertyDetails(state, action.payload, action.timestamp)

  case 'LOAD_PROPERTY_DETAILS_AND_BECOME_READY':
    if (state.mode !== 'Loading') {
      return state
    }
    return {
      ...loadPropertyDetails(state, action.payload, action.timestamp),
      mode: 'Ready'
    }

  case 'LOAD_ALL_PROPERTY_SUMMARIES':
    if (state.mode !== 'Loading') {
      return state
    }
    return loadAllPropertySummaries(state, action.payload, action.timestamp)

  case 'LOAD_ALL_PROPERTY_SUMMARIES_AND_BECOME_READY':
    if (state.mode !== 'Loading') {
      return state
    }
    return {
      ...loadAllPropertySummaries(state, action.payload, action.timestamp),
      mode: 'Ready'
    }

  case 'FINISH_LOADING':
    if (state.mode !== 'Loading') {
      return state
    }
    return {
      ...state,
      mode: 'Ready'
    }

  case 'ERROR_PROPERTIES':
    if (state.mode !== 'Loading') {
      return state
    }
    return { ...state, mode: 'Error', errorMessage: action.message }

  case 'REFRESH_PROPERTY_INFO': {
    if (state.mode !== 'Ready') {
      return state
    }

    let needsSummaryRefresh = []
    let needsDetailsRefresh = []
    for (let entry of Object.entries(state.properties)) {
      const [propertyId, property]: [string, any] = entry
      if (property.summaryTimestamp) {
        let summaryAge = action.timestamp - property.summaryTimestamp
        if (summaryAge > MAX_PROPERTY_INFO_AGE) {
          needsSummaryRefresh.push(propertyId)
        }
      }
      if (property.detailsTimestamp) {
        let detailsAge = action.timestamp - property.detailsTimestamp
        if (detailsAge > MAX_PROPERTY_INFO_AGE) {
          needsDetailsRefresh.push(propertyId)
        }
      }
    }

    if (needsSummaryRefresh.length > 0 || needsDetailsRefresh.length > 0) {
      return {
        ...enqueueCommand(state, {
          type: 'CMD_REFRESH_PROPERTY_INFO',
          summaryPropertyIds: needsSummaryRefresh,
          detailsPropertyIds: needsDetailsRefresh
        }),
        mode: 'Loading'
      }
    }
    return state
  }
  default:
    return state
  }
}

export function StoreProvider(props: any) {
  const useElmishResult = useElmishWithReinspect(reducer, initialState, runCommand, 'PropertyStore')
  const dispatch: any = useElmishResult[1]

  React.useEffect(() => {
    // Start the periodic refresh timer (only happens once, when the store is mounted.)
    let timeoutId = setTimeout(function refreshProperties() {
      // @ts-ignore
      dispatch({ type: 'REFRESH_PROPERTY_INFO', timestamp: getCurrentTimestamp() })
      setTimeout(refreshProperties, PROPERTY_INFO_POLL_INTERVAL * 1000)
    }, PROPERTY_INFO_POLL_INTERVAL * 1000)
    return () => {
      clearTimeout(timeoutId)
    }
  }, [])

  return <Store.Provider value={useElmishResult}>{props.children}</Store.Provider>
}

export function MockStoreProvider(props: any) {
  const mockUseElmishResult = [props.state, props.dispatch]
  return <Store.Provider value={mockUseElmishResult}>{props.children}</Store.Provider>
}
